// Standing history — every application-status change on the raw user document.
//
// Was a collapsed accordion holding a 500px-wide MUI `Table` with its own header row.
// Now hairline `Row`s: two facts per entry is a row, not a table, and the "Suspended: N
// times" line it opened with is a count, so it moves into the section header where the
// other counts on this page live.
//
// This overlaps the event log above it, which already lists every one of these rows
// interleaved with the document events. Kept for the aggregate — but the right end state
// is that count in the event log's header and this section gone.
import { Box, Typography } from '../../../ui/core';
import { EventIcon } from '../../../ui/icons';
import { scrollY } from '../../../ui/styles';
import { SectionCard } from '../../../pages/DriverProfile/components';
import { Row, TrailingMeta, Meta } from '../../../pages/UserProfile/parts';

const DriverStatusHistoryCard = ({ userRawUnsafe }) => {
  // `userRawUnsafe` is null whenever the user document did not come back with the page —
  // the old version read straight through it and took the whole tab down with it.
  const updates = userRawUnsafe?.driverApplicationStatusUpdates;
  if (!updates?.length) {
    return null;
  }

  const suspensions = updates.filter((update) => update.status === 'suspended').length;
  // Copied, not sorted in place: `sort` mutates, and this array belongs to the parsed
  // user document that the rest of the page also reads.
  const newestFirst = [...updates].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

  return (
    <SectionCard
      title="Standing history"
      icon={<EventIcon sx={{ fontSize: 17, color: 'text.secondary', flexShrink: 0 }} />}
      meta={
        <TrailingMeta>
          <Meta>
            {newestFirst.length} change{newestFirst.length === 1 ? '' : 's'}
            {suspensions > 0 ? ` · suspended ${suspensions}×` : ''}
          </Meta>
        </TrailingMeta>
      }
    >
      <Box sx={{ maxHeight: 400, ...scrollY }}>
        {newestFirst.map((update, i) => (
          <Row
            key={update.timestamp}
            first={i === 0}
            label={update.status}
            // US/Central, kept from the original: these timestamps are read against
            // support's own working hours, not the driver's.
            right={
              <Typography sx={{ fontSize: '11.5px', color: 'text.secondary', whiteSpace: 'nowrap' }}>
                {new Date(update.timestamp).toLocaleString('en-US', { timeZone: 'America/Chicago' })}
              </Typography>
            }
          />
        ))}
      </Box>
    </SectionCard>
  );
};

export default DriverStatusHistoryCard;
